import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Sparkles, Download, Plus, ChevronLeft, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { useChainStore } from "../data/chainStore";
import { useAuth } from "../data/authStore";
import { useDealRoomRealtime } from "../hooks/useDealRoomRealtime";
import { useDealAccess } from "../hooks/useDealAccess";
import { api } from "../lib/api";
import { clientPreflight, uploadErrorPresentation } from "../lib/uploadErrors";
import { Sidebar } from "../components/Sidebar";
import {
  PropertyHeader, StageProgression, ContentTabs, FlowCard, FlowComposer,
  BottomKPIBar, LIGHT, cardStyle, fmtTime, fmtDay, detectKind, KIND_META,
  stripKindPrefix,
} from "../components/transaction-room-v2/RoomFlow";
import {
  DealHealthSummary, AwaitingCard, NextKeyDateCard,
  ExchangeReadinessCard, AIInsightCard, QuickActionsCard,
} from "../components/transaction-room-v2/RoomIntel";
import { FlowIntelligencePanel, MomentumChip, TypingIndicator } from "../components/transaction-room-v2/FlowIntelligence";
import { MomentumEngine } from "../components/transaction-room-v2/MomentumEngine";
import { ProgressionTimeline } from "../components/transaction-room-v2/ProgressionTimeline";
import { RoomHistory } from "../components/transaction-room-v2/RoomHistory";
import { OperationalVitals } from "../components/dealroom/OperationalVitals";
import { DealRoomReturnRibbon } from "../components/transaction-room-v2/DealRoomReturnRibbon";
import { GoldenPath } from "../components/transaction-room-v2/GoldenPath";
import { ParticipantActivityPanel } from "../components/transaction-room-v2/ParticipantActivityPanel";
import { SinceLastVisit } from "../components/transaction-room-v2/SinceLastVisit";
import {
  ParticipantsLiveCard, DocumentRequestsCard, DocumentRequestsPanel,
} from "../components/transaction-room-v2/RoomCollab";
import { ChaseConfirmDrawer } from "../components/transaction-room-v2/ChaseConfirmDrawer";
import { DealRoomMuteMenu, MutedPill } from "../components/transaction-room-v2/DealRoomMuteMenu";
import { DealRoomStatusLine } from "../components/transaction-room-v2/DealRoomStatusLine";
import ParticipantsModal from "../components/dealroom/ParticipantsModal";
import { DealIntelligenceRail } from "../components/dealroom/DealIntelligenceRail";
import { DealCoordinationPanel } from "../components/dealroom/DealCoordinationPanel";
import { SellerConfidencePanel } from "../components/dealroom/SellerConfidencePanel";
import { SolicitorPortalInvitesPanel } from "../components/dealroom/SolicitorPortalInvitesPanel";
import { OperationalSummaryLine } from "../components/dealroom/OperationalSummaryLine";
import { RecommendedNextActions } from "../components/dealroom/RecommendedNextActions";
import { DocumentHub } from "../components/dealroom/DocumentHub";
import { InternalNotesPanel } from "../components/dealroom/InternalNotesPanel";
import { DealPlaybooksPanel } from "../components/playbooks/DealPlaybooksPanel";
import { DealRecoveryPlanPanel } from "../components/playbooks/DealRecoveryPlanPanel";
import { DealForecastTrajectoryPanel } from "../components/playbooks/DealForecastTrajectoryPanel";
import { DealMemoryPanel } from "../components/playbooks/DealMemoryPanel";
import { DealScenarioPanel } from "../components/playbooks/DealScenarioPanel";
import { DealRhythmPanel } from "../components/playbooks/DealRhythmPanel";
import { DealDecisionPanel } from "../components/playbooks/DealDecisionPanel";
import { SinceLastVisitStrip } from "../components/dealroom/SinceLastVisitStrip";
import { CompactBlockerRail } from "../components/dealroom/CompactBlockerRail";

/* ═══════════════════════════════════════════════════════════════════
   ChainFlow Transaction Room v3 — light-mode hybrid
   ─────────────────────────────────────────────────────────────────── */

const _ts = (v) => (typeof v === "number" ? v : v ? Date.parse(v) : 0);

/* ─── Empty deal state ─────────────────────────────────────────── */
const EmptyState = () => (
  <div className="flex-1 flex flex-col items-center justify-center text-center p-10">
    <div
      className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5"
      style={{ background: "linear-gradient(135deg, #8B5CF6, #6366F1)", boxShadow: "0 16px 32px -10px rgba(99,102,241,0.6)" }}
    >
      <Sparkles size={26} className="text-white" />
    </div>
    <p className="text-[20px] font-bold" style={{ color: LIGHT.textPrimary }}>Pick a deal to open</p>
    <p className="text-[13px] mt-2 max-w-md" style={{ color: LIGHT.textMuted }}>
      Choose a transaction from the Flow Centre to inspect its operational state.
    </p>
    <Link
      to="/dashboard"
      className="mt-5 h-11 px-5 rounded-xl text-white text-[13px] font-bold inline-flex items-center gap-2"
      style={{ background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)", boxShadow: "0 14px 32px -10px rgba(99,102,241,0.55)" }}
    >
      Go to Flow Centre
    </Link>
  </div>
);

/* ─── Flow stream renderer ────────────────────────────────────── */
/* ─── Operational day-group classifier (iter-181) ──────────────
 *  Groups timeline events into the three calm operational buckets
 *  the brief mandates — never raw "Mon 12 May" timestamps.
 */
const operationalDayBucket = (ts) => {
  if (!ts) return "earlier";
  const d = new Date(ts);
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  // Start of the operator's week = Monday 00:00 (UK convention).
  const dow = (now.getDay() + 6) % 7; // 0=Mon .. 6=Sun
  const startOfWeek = startOfToday - dow * 24 * 60 * 60 * 1000;
  if (d.getTime() >= startOfToday)     return "today";
  if (d.getTime() >= startOfYesterday) return "yesterday";
  if (d.getTime() >= startOfWeek)      return "this_week";
  return "earlier";
};
const BUCKET_LABEL = {
  today:     "Today",
  yesterday: "Yesterday",
  this_week: "Earlier this week",
  earlier:   "Earlier",
};

/* Milestone-kind detector — these breathe more (larger card surface,
 *  stronger title, no compression). Everything else stays calm. */
const isMilestoneKind = (kind) =>
  ["milestone", "document_received", "deadline", "risk"].includes(kind);

const FlowStream = ({ deal, contacts, currentUserId, onUpload, onChaseRequest }) => {
  // Combine messages + activity into a unified time-ordered stream
  const items = useMemo(() => {
    const msgs = (deal.messages || []).map((m) => ({ ...m, _src: "msg" }));
    const acts = (deal.activity || []).map((a) => ({
      id: a.id || `act-${_ts(a.at)}-${(a.text || "").slice(0, 8)}`,
      body: a.text || a.kind,
      at: _ts(a.at),
      kind: "system",
      from: a.actor || null,
      _src: "act",
    }));
    return [...msgs, ...acts]
      .filter((x) => x.body)
      .sort((a, b) => _ts(b.at) - _ts(a.at)); // newest first
  }, [deal.messages, deal.activity]);

  if (items.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-16 text-center">
        <p className="text-[14px] font-semibold" style={{ color: LIGHT.textPrimary }}>The Flow is quiet right now</p>
        <p className="text-[12px] mt-1.5 max-w-md" style={{ color: LIGHT.textMuted }}>
          Add a milestone, a risk, or a document update below to get the team moving.
        </p>
      </div>
    );
  }

  // Group items into the four operational buckets — Today /
  // Yesterday / Earlier this week / Earlier. Preserves newest-first
  // ordering inside each bucket.
  const buckets = { today: [], yesterday: [], this_week: [], earlier: [] };
  items.forEach((m) => { buckets[operationalDayBucket(_ts(m.at))].push(m); });

  /* Build a senderName/Role pair for the card footer. */
  const senderInfoFor = (m) => {
    if (m._src === "act") return { name: m.from || "ChainFlow", role: null };
    if (m.from === currentUserId) return { name: "You", role: null };
    const c = contacts.find((x) => x.id === m.from);
    if (!c) return { name: "Workspace", role: null };
    const name = `${c.firstName || c.first_name || ""} ${c.lastName || c.last_name || ""}`.trim() || c.email || "Member";
    return { name, role: c.role || null };
  };

  /* Render a single timeline entry. Milestone kinds breathe more —
   *  larger card surface, stronger title, no compression. */
  const renderCard = (m) => {
    const kind = detectKind(m);
    const meta = KIND_META[kind] || KIND_META.update;
    const body = stripKindPrefix(m.body || "");
    const sender = senderInfoFor(m);
    const footerText = `By ${sender.name}${sender.role ? ` (${sender.role})` : ""}`;
    const isMilestone = isMilestoneKind(kind);

    // Inline contextual actions
    let action = null;
    if (kind === "document_request") {
      action = (
        <button
          type="button"
          onClick={onUpload}
          data-testid={`dealroom-flow-action-${m.id}`}
          className="h-9 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5"
          style={{ background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: LIGHT.textPrimary }}
        >
          <Download size={11} className="rotate-180" /> Upload Document
        </button>
      );
    } else if (kind === "document_received") {
      const fnMatch = body.match(/([\w\s\-]+\.(pdf|docx?|xlsx?|jpe?g|png|csv))/i);
      action = fnMatch ? (
        <span className="h-9 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5"
          style={{ background: "#F8FAFC", border: `1px solid ${LIGHT.border}`, color: LIGHT.textPrimary }}>
          <FileText size={11} /> {fnMatch[1]}
        </span>
      ) : null;
    } else if (kind === "risk") {
      action = (
        <button
          type="button"
          onClick={() => onChaseRequest?.({ id: `flow-${m.id}`, title: m.body, source: "flow_card_risk" })}
          data-testid={`dealroom-flow-action-${m.id}`}
          className="h-9 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5"
          style={{ background: "#FFFFFF", border: `1px solid ${LIGHT.border}`, color: LIGHT.textPrimary }}
        >
          Chase
        </button>
      );
    }

    return (
      <div
        key={m.id}
        className={isMilestone ? "" : "opacity-95"}
        data-flow-milestone={isMilestone ? "1" : "0"}
      >
        <FlowCard
          kind={kind}
          day={null}                          /* day is shown in the bucket header */
          time={fmtTime(m.at)}
          title={meta.title}
          body={body}
          footer={footerText}
          action={action}
          testid={`dealroom-flow-card-${m.id}`}
        />
      </div>
    );
  };

  /* Bucket header — calm uppercase rhythm matching the sidebar
   *  section labels. Soft slate-500, never shouts. */
  const renderBucketHeader = (label, count) => (
    <header className="flex items-center gap-2 px-2 pt-1" data-testid={`dealroom-flow-bucket-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</span>
      <span className="h-px flex-1" style={{ background: "rgba(15,23,42,0.06)" }} />
      <span className="text-[10.5px] font-medium text-slate-400 tabular-nums">{count}</span>
    </header>
  );

  return (
    <div className="space-y-6">
      {(["today", "yesterday", "this_week", "earlier"]).map((bucketKey) => {
        const rows = buckets[bucketKey];
        if (!rows.length) return null;
        return (
          <section key={bucketKey} className="space-y-3">
            {renderBucketHeader(BUCKET_LABEL[bucketKey], rows.length)}
            <div className="space-y-3.5">
              {rows.map(renderCard)}
            </div>
          </section>
        );
      })}
    </div>
  );
};

/* ─── Mobile FAB — primary "compose update" action ────────────── */
const MobileFAB = ({ onTap }) => createPortal(
  <button
    type="button"
    onClick={onTap}
    aria-label="Add update to Flow"
    className="md:hidden fixed right-4 z-[60] flex items-center justify-center transition-transform active:scale-95"
    style={{
      bottom: "calc(74px + env(safe-area-inset-bottom, 0px))",
      width: 56,
      height: 56,
      borderRadius: 18,
      background: "linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)",
      color: "#FFFFFF",
      boxShadow:
        "0 14px 28px -10px rgba(99,102,241,0.55), 0 2px 0 rgba(255,255,255,0.10) inset",
      border: "1px solid rgba(255,255,255,0.20)",
    }}
    data-testid="dealroom-mobile-fab"
  >
    <Plus size={22} strokeWidth={2.6} />
  </button>,
  document.body,
);

/* ─── Mobile sticky compact header ─────────────────────────────── */
const MobileStickyHeader = ({ deal, property, onBack }) => {
  const stageMap = {
    lead: "Lead", qualified: "Qualified", viewing: "Viewing",
    offer: "Offer Accepted", aml: "AML", memo: "Memorandum",
    solicitors: "Solicitors", searches: "Searches",
    mortgage_app: "Mortgage App", survey: "Survey",
    enquiries: "Enquiries", mortgage_offer: "Mortgage Offer",
    exchange_ready: "Pre-Exchange", exchanged: "Exchanged",
    completed: "Completed",
  };
  const stage = stageMap[deal.stage] || deal.stage || "—";

  return (
    <div
      className="md:hidden sticky top-0 z-30 flex items-center gap-2 px-3 h-12"
      style={{
        background: "rgba(255,255,255,0.92)",
        borderBottom: `1px solid ${LIGHT.border}`,
        backdropFilter: "blur(14px) saturate(140%)",
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
      }}
      data-testid="dealroom-mobile-sticky-header"
    >
      <button
        type="button"
        onClick={onBack}
        aria-label="Back to deals"
        className="w-9 h-9 -ml-1 rounded-lg inline-flex items-center justify-center active:scale-95 transition-transform"
        style={{ color: LIGHT.textPrimary }}
        data-testid="dealroom-mobile-back"
      >
        <ChevronLeft size={18} strokeWidth={2.4} />
      </button>
      <div className="min-w-0 flex-1">
        <p
          className="text-[12.5px] font-bold truncate leading-tight"
          style={{ color: LIGHT.textPrimary }}
        >
          {property?.address || deal.name || "Transaction"}
        </p>
        <p className="text-[10px] tabular-nums leading-tight mt-0.5" style={{ color: LIGHT.textMuted }}>
          {/* iter-alignment · P0 · Price SSOT Audit (2026-02-28)
              — Deal mini-header shows deal Agreed Price only. */}
          {fmtMoney(deal.agreedPrice || deal.value)}
          <span className="mx-1">·</span>
          CF-{(deal.id || "").slice(0, 6).toUpperCase()}
        </p>
      </div>
      <span
        className="text-[10px] font-bold uppercase tracking-[0.12em] px-2 h-[22px] rounded-full inline-flex items-center shrink-0"
        style={{
          background: "linear-gradient(135deg, rgba(139,92,246,0.14), rgba(99,102,241,0.10))",
          color: "#6366F1",
          border: "1px solid rgba(99,102,241,0.22)",
        }}
      >
        {stage}
      </span>
      <MutedPill dealId={deal?.id} />
      <DealRoomMuteMenu deal={deal} variant="mobile" />
    </div>
  );
};

const fmtMoney = (n) => {
  if (!n) return "—";
  const num = typeof n === "number" ? n : parseInt(String(n).replace(/[^0-9]/g, ""), 10) || 0;
  if (!num) return "—";
  return `£${num.toLocaleString()}`;
};

/* ═══════════════════════════════════════════════════════════════════
   PAGE
   ─────────────────────────────────────────────────────────────────── */
export default function DealRoomPage() {
  const { dealId } = useParams();
  const { deals, contacts, findDeal, findProperty, sendMessage } = useChainStore();
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("flow");
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [docRequests, setDocRequests] = useState([]);
  const [intel, setIntel] = useState(null);
  const [momentum, setMomentum] = useState(null);
  // v2-sourced Flow data (so React state-driven re-render is reliable,
  // not mutation-based). When non-null these overlay deal.activity/messages.
  const [v2Activity, setV2Activity] = useState(null);
  const [v2Messages, setV2Messages] = useState(null);
  const [seenBookmark, setSeenBookmark] = useState(null); // {prev_bookmark_ts, bookmark_ts, is_first_visit}
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [chaseItem, setChaseItem] = useState(null);
  const [recentlyChasedIds, setRecentlyChasedIds] = useState({});
  const [presetKind, setPresetKind] = useState(null);
  const [presetText, setPresetText] = useState(null);
  const [presetTick, setPresetTick] = useState(0);
  const fileInputRef = React.useRef(null);

  // iter-184 P1 — caller's effective capability set on THIS deal.
  // Threads `can_comment / can_upload_documents / can_view_internal_notes`
  // into upload, composer, and internal-notes surfaces below.
  const access = useDealAccess(deal?.id);

  // iter-176 — Removed legacy auto-redirect that turned `/deal-rooms`
  // into "Resume last opened deal". With the new index page mounted at
  // `/deal-rooms`, this page is only ever reached with an explicit
  // `:dealId` in the URL. If the dealId can't be resolved we render the
  // page's normal "deal not found" state rather than silently steering
  // the user into someone else's transaction.
  const effectiveId = dealId;
  const deal = findDeal(effectiveId);

  const reloadDeal = React.useCallback(async () => {
    if (!deal?.id) return;
    const since = seenBookmark?.prev_bookmark_ts;
    const intelUrl = since
      ? `/api/deals/${deal.id}/intelligence?since=${encodeURIComponent(since)}`
      : `/api/deals/${deal.id}/intelligence`;
    const [t, d, p, r, i, m, v2act, v2parts, v2msgs, v2docs] = await Promise.all([
      api.get(`/api/tasks?deal_id=${deal.id}`).catch(() => ({ data: [] })),
      api.get(`/api/deals/${deal.id}/documents`).catch(() => ({ data: [] })),
      api.get(`/api/deals/${deal.id}/participants-enriched`).catch(() => ({ data: [] })),
      api.get(`/api/deals/${deal.id}/document-requests`).catch(() => ({ data: [] })),
      api.get(intelUrl).catch(() => ({ data: null })),
      api.get(`/api/deals/${deal.id}/momentum`).catch(() => ({ data: null })),
      // v2 dual-read (Postgres) — soft-fails to [] if not configured
      api.get(`/api/v2/deals/${deal.id}/activity?limit=200`).catch(() => ({ data: [] })),
      api.get(`/api/v2/deals/${deal.id}/participants`).catch(() => ({ data: [] })),
      api.get(`/api/v2/deals/${deal.id}/messages?limit=200`).catch(() => ({ data: [] })),
      api.get(`/api/v2/deals/${deal.id}/documents`).catch(() => ({ data: [] })),
    ]);
    setTasks(Array.isArray(t.data) ? t.data : []);
    // Documents: prefer v2 when present, fall back to v1
    const v1Docs = Array.isArray(d.data) ? d.data : [];
    const v2DocsArr = Array.isArray(v2docs.data) ? v2docs.data : [];
    setDocuments(v2DocsArr.length > 0
      ? v2DocsArr.map((dd) => ({
          id: dd.id, deal_id: dd.deal_id, name: dd.name,
          folder: dd.folder || "Miscellaneous", category: dd.folder || null,
          storage_path: dd.storage_path, size_bytes: dd.size_bytes || 0,
          size: dd.size_bytes || 0, mime_type: dd.mime_type, type: dd.mime_type,
          uploaded_by: dd.uploaded_by_id || null, created_at: dd.created_at, _src: "v2",
        }))
      : v1Docs);
    // Participants: prefer v2 (real external parties) when present
    const v1Parts = Array.isArray(p.data) ? p.data : [];
    const v2PartsArr = Array.isArray(v2parts.data) ? v2parts.data : [];
    setParticipants(v2PartsArr.length > 0
      ? v2PartsArr.map((pp) => ({
          id: pp.id, user_id: pp.id,
          full_name: pp.full_name, email: pp.email || null, phone: pp.phone || null,
          role: pp.participant_type,
          role_label: ({
            buyer: "Buyer", seller: "Seller",
            buyer_solicitor: "Buyer Solicitor", seller_solicitor: "Seller Solicitor",
            mortgage_broker: "Mortgage Broker", negotiator: "Negotiator",
            progressor: "Progressor", other: "Other",
          })[pp.participant_type] || pp.participant_type,
          organisation_name: pp.organisation_name || null,
          last_seen_at: pp.last_active_at || null,
          responsiveness: "responsive",
          is_internal: ["negotiator", "progressor"].includes(pp.participant_type),
          _src: "v2",
        }))
      : v1Parts);
    setDocRequests(Array.isArray(r.data) ? r.data : []);
    setIntel(i.data || null);
    setMomentum(m.data || null);
    // v2 activity + messages — surface via state so React re-renders the
    // Flow stream reliably (mutation alone wouldn't trigger updates).
    const v2ActArr = Array.isArray(v2act.data) ? v2act.data : [];
    const v2MsgArr = Array.isArray(v2msgs.data) ? v2msgs.data : [];
    const kindMap = {
      document_upload: "doc", document_request: "doc", message: "msg",
      risk_flagged: "risk", risk_cleared: "risk", milestone_completed: "milestone",
      participant_added: "participant", participant_removed: "participant",
      note: "note", task_created: "task", task_completed: "task",
      status_change: "stage", stage_change: "stage", system: "system",
    };
    setV2Activity(v2ActArr.length > 0 ? v2ActArr.map((a) => ({
      id: a.id, kind: kindMap[a.activity_type] || a.activity_type || "system",
      at: a.created_at, actor: a.created_by_id || null, text: a.title,
      description: a.description || null, meta: a.meta || {}, _src: "v2",
    })) : null);
    setV2Messages(v2MsgArr.length > 0 ? v2MsgArr.map((m) => ({
      id: m.id, body: m.body, text: m.body,
      from: m.author_id || null, internal: !!m.internal,
      at: m.created_at, _src: "v2",
    })) : null);
  }, [deal, seenBookmark?.prev_bookmark_ts]);

  // On Deal Room mount, atomically rotate the user's bookmark for this deal
  // and freeze `prev_bookmark_ts` for the session so SinceLastVisit stays
  // stable during polling.
  useEffect(() => {
    if (!deal?.id) return;
    let cancelled = false;
    api.post(`/api/deals/${deal.id}/seen`).then((r) => {
      if (!cancelled && r?.data) setSeenBookmark(r.data);
    }).catch(() => null);
    return () => { cancelled = true; };
  }, [deal?.id]);

  // Manual "Mark caught up" — rotates bookmark to now, clears the panel
  const handleMarkSeen = React.useCallback(async () => {
    if (!deal?.id) return;
    try {
      const r = await api.post(`/api/deals/${deal.id}/seen`);
      if (r?.data) setSeenBookmark(r.data);
      reloadDeal();
    } catch {
      // calm-fail
    }
  }, [deal?.id, reloadDeal]);

  useEffect(() => {
    if (!deal?.id) return undefined;
    let cancelled = false;
    const tick = async () => {
      // Fire heartbeat alongside data reload — keeps presence fresh
      api.post(`/api/deals/${deal.id}/heartbeat`).catch(() => null);
      if (!cancelled) await reloadDeal();
    };
    tick();
    // Relaxed polling cadence — Realtime carries the live signal, polling
    // is a reconnect/recovery safety net only.
    const i = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(i); };
  }, [deal?.id, reloadDeal]);

  // iter-176 — Removed the auto-`nav` that redirected `/deal-rooms`
  // → `/deal-rooms/{first-deal-id}` whenever the URL lacked an id.
  // The Deal Rooms index page is now the canonical landing surface;
  // this page is only reached with an explicit dealId in the URL.

  // ─── Realtime · Supabase channel for this Deal Room ───────────────────────
  // We use Realtime as a low-latency *signal* — when an INSERT lands on
  // deal_activity or messages for this deal_id, we re-call reloadDeal()
  // which fetches authoritative data through FastAPI (which enforces RBAC).
  // The polling fallback below still runs at a relaxed 30s cadence as a
  // reconnect safety net.
  useDealRoomRealtime(deal?.id, React.useCallback(() => {
    if (deal?._v2only) reloadDeal();
  }, [deal?._v2only, reloadDeal]));

  const property = deal ? findProperty(deal.propertyId) : null;

  // Overlay v2 activity/messages onto the deal object so child components
  // pick them up via the existing `deal.activity` / `deal.messages` props.
  // Falls back to the chain-store deal when v2 data isn't loaded yet.
  const displayDeal = useMemo(() => {
    if (!deal) return deal;
    if (v2Activity === null && v2Messages === null) return deal;
    return {
      ...deal,
      activity: v2Activity !== null ? v2Activity : (deal.activity || []),
      messages: v2Messages !== null ? v2Messages : (deal.messages || []),
    };
  }, [deal, v2Activity, v2Messages]);

  const counts = useMemo(() => ({
    tasks: tasks.filter((t) => t.status !== "done").length,
    documents: documents.length,
    participants: participants.length,
  }), [tasks, documents, participants]);

  const lastActivityTs = useMemo(() => {
    const acts = displayDeal?.activity || [];
    const msgs = displayDeal?.messages || [];
    const all = [...acts.map((a) => _ts(a.at)), ...msgs.map((m) => _ts(m.at))];
    return all.length ? Math.max(...all) : null;
  }, [displayDeal]);

  if (!deals.length || !deal) {
    return (
      <div className="min-h-screen flex" style={{ background: LIGHT.bg }} data-testid="dealroom-empty">
        <Sidebar />
        <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex-1 flex"><EmptyState /></main>
      </div>
    );
  }

  const handleSend = async (body) => {
    try {
      await sendMessage(deal.id, { from: user?.id || deal.agentId, body });
      // v2-only deals need an immediate reload so the new message + auto-logged
      // activity row appear without waiting for the 12s polling tick.
      if (deal?._v2only) reloadDeal();
    } catch {
      toast.error("Could not send update");
    }
  };

  const handleMarkAwaitingResolved = async (item) => {
    if (item.source === "task" && item.taskId) {
      try {
        await api.patch(`/api/tasks/${item.taskId}`, { status: "done" }).catch(() => null);
        setTasks((prev) => prev.map((t) => t.id === item.taskId ? { ...t, status: "done" } : t));
        await sendMessage(deal.id, {
          from: user?.id || deal.agentId,
          body: `[milestone] ${item.title} — resolved.`,
        });
        toast.success("Marked resolved + posted to Flow");
      } catch {
        toast.error("Could not mark resolved");
      }
    }
  };

  const presetComposer = (kind, text) => {
    setPresetKind(kind);
    setPresetText(text);
    setPresetTick((n) => n + 1);
    setTab("flow");
  };

  const handleQuickAction = (id) => {
    if (id === "request") {
      setTab("documents");
      toast.message("Choose the document to request.");
    } else if (id === "update") {
      presetComposer("update", "");
      toast.message("Add an operational update to the Flow.");
    } else if (id === "milestone") {
      presetComposer("milestone", "");
      toast.message("Log a milestone (e.g. mortgage offer received).");
    } else if (id === "chase") {
      presetComposer("risk", "Awaiting response — chasing for an update.");
      toast.message("Chase drafted — review and send.");
    }
  };

  /* ─── Momentum Engine action router ─────────────────────────────
     Maps the per-signal quick actions emitted by /api/deals/{id}/momentum
     onto existing operational handlers. Stays calm: every action is
     either local UI nudge (preset composer) or fires existing API
     calls. No new endpoints are introduced. */
  const handleMomentumAction = (signal, action) => {
    const id = action?.id;
    if (id === "chase") {
      // Construct a synthetic "awaiting" item to feed the existing
      // ChaseConfirmDrawer. owner+title preserved verbatim.
      setChaseItem({
        id: signal.id,
        title: signal.title,
        owner: signal.owner,
        source: "momentum",
      });
      return;
    }
    if (id === "request_eta") {
      presetComposer("update", `Could you share an ETA on ${signal.title.toLowerCase()}?`);
      toast.message("ETA chase drafted — review and send.");
      return;
    }
    if (id === "escalate") {
      api.post(`/api/deals/${deal.id}/awaiting/escalate`, {
        item_id: signal.id,
        title: signal.title,
        owner: signal.owner,
      }).then(() => {
        toast.message("Escalated as risk — visible across Flow");
        reloadDeal();
      }).catch(() => toast.error("Could not escalate"));
      return;
    }
    if (id === "schedule_call") {
      presetComposer("update", `Proposed a 15-minute call to unblock ${signal.title.toLowerCase()}.`);
      toast.message("Schedule note drafted — review and send.");
      return;
    }
    if (id === "re_engage" || id === "add_update") {
      presetComposer("update", `Update on ${signal.title.toLowerCase()}: `);
      toast.message("Update drafted — keep it concise.");
      return;
    }
    if (id === "notify_seller") {
      presetComposer("update", `Heads up on ${signal.title.toLowerCase()}. Confirming our exchange target remains realistic.`);
      toast.message("Seller notification drafted.");
      return;
    }
    if (id === "revise_target") {
      presetComposer("milestone", `Revising exchange target — ${signal.title.toLowerCase()} requires alignment.`);
      toast.message("Target revision drafted.");
      return;
    }
    if (id === "add_note") {
      presetComposer("update", `Note on ${signal.title.toLowerCase()}: `);
      toast.message("Note drafted in the composer.");
      return;
    }
    // Fallback — calm
    presetComposer("update", `Action on ${signal.title.toLowerCase()}: `);
  };

  /* ─── Awaiting inline action handlers ─── */
  const refreshDealActivity = async () => {
    // Easiest reliable refresh: re-fetch deal-bound resources
    await reloadDeal();
  };

  const handleAwaitingChase = (it) => {
    // Open the confirm drawer instead of firing the chase immediately.
    setChaseItem(it);
  };

  const handleChaseSent = (data) => {
    const it = chaseItem;
    setChaseItem(null);
    if (!it) return;
    // 8s "Sent ✓" state
    setRecentlyChasedIds((m) => ({ ...m, [it.id]: Date.now() + 8000 }));
    setTimeout(() => {
      setRecentlyChasedIds((m) => {
        const out = { ...m };
        delete out[it.id];
        return out;
      });
    }, 8000);
    if (data?.email_status === "sent") toast.success("Chase email sent · logged to Flow");
    else if (data?.email_status === "logged") toast.success("Chase logged to Flow");
    else if (data?.email_status === "failed") toast.error("Email failed — chase still logged to Flow");
    else toast.success("Chase logged to Flow");
    reloadDeal();
  };

  const handleAwaitingEscalate = async (it) => {
    try {
      await api.post(`/api/deals/${deal.id}/awaiting/escalate`, {
        item_id: it.id,
        title: it.title,
        owner: it.owner,
      });
      toast.message(`Escalated as risk — visible across Flow`);
      await refreshDealActivity();
    } catch {
      toast.error("Could not escalate");
    }
  };

  const handleAwaitingAddUpdate = (it) => {
    setTab("flow");
    presetComposer("update", `Update on ${it.title}: `);
  };

  const handleAwaitingRequestDoc = (it) => {
    if (it.source === "document" && it.docId && it.docId !== "default") {
      handleCreateRequest({ doc_type: it.docId });
    } else {
      setTab("documents");
      toast.message("Pick the document type to request.");
    }
  };

  const handleAwaitingReassign = () => {
    setParticipantsOpen(true);
    toast.message("Choose someone to take this on.");
  };

  /* ─── Document Request handlers ─── */
  const refreshRequests = async () => {
    try {
      const { data } = await api.get(`/api/deals/${deal.id}/document-requests`);
      setDocRequests(Array.isArray(data) ? data : []);
    } catch { /* noop */ }
  };

  const handleCreateRequest = async (payload) => {
    try {
      await api.post(`/api/deals/${deal.id}/document-requests`, payload);
      await refreshRequests();
      // Refresh deal activity to surface in Flow
      toast.success(`Requested ${payload.doc_type.toUpperCase()} — posted to Flow`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create request");
    }
  };

  const handleApproveRequest = async (req) => {
    try {
      await api.patch(`/api/deals/${deal.id}/document-requests/${req.id}`, { status: "approved" });
      await refreshRequests();
      toast.success(`${req.label} approved`);
    } catch { toast.error("Could not approve"); }
  };

  const handleRejectRequest = async (req) => {
    try {
      await api.patch(`/api/deals/${deal.id}/document-requests/${req.id}`, { status: "rejected" });
      await refreshRequests();
      toast.message(`${req.label} sent back for re-upload`);
    } catch { toast.error("Could not reject"); }
  };

  const handleCancelRequest = async (req) => {
    try {
      await api.patch(`/api/deals/${deal.id}/document-requests/${req.id}`, { status: "cancelled" });
      await refreshRequests();
      toast.message(`${req.label} request cancelled`);
    } catch { toast.error("Could not cancel"); }
  };

  const handleDeleteRequest = async (req) => {
    try {
      await api.delete(`/api/deals/${deal.id}/document-requests/${req.id}`);
      await refreshRequests();
    } catch { toast.error("Could not delete"); }
  };

  const handleFulfillRequest = async (req, documentId) => {
    try {
      await api.post(`/api/deals/${deal.id}/document-requests/${req.id}/fulfill`, { document_id: documentId });
      await refreshRequests();
      toast.success(`${req.label} linked — awaiting approval`);
    } catch { toast.error("Could not link upload"); }
  };

  const handleFileUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    // Client preflight — calm instant feedback before bytes hit the wire.
    const pre = clientPreflight(file);
    if (!pre.ok) {
      toast.error(`${pre.title} · ${file.name}`, { description: pre.reason });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    // v2 deals: real multipart upload to Supabase Storage via /api/v2.
    if (deal?._v2only) {
      const form = new FormData();
      form.append("file", file);
      form.append("folder", "Miscellaneous");
      const toastId = toast.loading(`Uploading ${file.name}…`);
      try {
        const { data } = await api.post(`/api/v2/deals/${deal.id}/documents`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (evt) => {
            if (evt.total) {
              const pct = Math.round((evt.loaded / evt.total) * 100);
              toast.loading(`Uploading ${file.name} · ${pct}%`, { id: toastId });
            }
          },
        });
        setDocuments((arr) => [{
          id: data.id, deal_id: data.deal_id, name: data.name,
          folder: data.folder || "Miscellaneous", category: data.folder || null,
          storage_path: data.storage_path, size_bytes: data.size_bytes || 0,
          size: data.size_bytes || 0, mime_type: data.mime_type, type: data.mime_type,
          uploaded_by: data.uploaded_by_id || null, created_at: data.created_at, _src: "v2",
        }, ...arr]);
        toast.success(`Uploaded ${data.name}`, { id: toastId });
        reloadDeal();
      } catch (err) {
        toast.dismiss(toastId);
        const { title, description } = uploadErrorPresentation(err, file.name);
        toast.error(title, { description });
      } finally {
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
      return;
    }
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/api/deals/${deal.id}/documents`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocuments((arr) => [data, ...arr]);
      toast.success(`Uploaded ${data.name}`);
    } catch (err) {
      const { title, description } = uploadErrorPresentation(err, file.name);
      toast.error(title, { description });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  /* Document download — mints a short-lived signed URL via /api/v2 and
     opens it in a new tab. For v1 documents falls through to the legacy
     storage_path (if it's an absolute URL) or the v1 download route. */
  const handleDocumentDownload = async (doc) => {
    if (!doc?.id) return;
    if (deal?._v2only || doc._src === "v2") {
      try {
        const { data } = await api.get(
          `/api/v2/deals/${deal.id}/documents/${doc.id}/download`
        );
        if (data?.url) window.open(data.url, "_blank", "noopener");
        else toast.error("Could not prepare download.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Download failed");
      }
      return;
    }
    if (doc.storage_path && /^https?:\/\//.test(doc.storage_path)) {
      window.open(doc.storage_path, "_blank", "noopener");
      return;
    }
    try {
      const { data } = await api.get(`/api/deals/${deal.id}/documents/${doc.id}/download`);
      if (data?.url) window.open(data.url, "_blank", "noopener");
    } catch {
      toast.error("Could not prepare download.");
    }
  };

  /* Document delete — v2 deals: hits /api/v2 which also removes the
     binary from Supabase Storage. */
  const handleDocumentDelete = async (doc) => {
    if (!doc?.id) return;
    if (deal?._v2only || doc._src === "v2") {
      try {
        await api.delete(`/api/v2/deals/${deal.id}/documents/${doc.id}`);
        setDocuments((arr) => arr.filter((d) => d.id !== doc.id));
        toast.success(`Removed ${doc.name}`);
        reloadDeal();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not delete document");
      }
    }
  };

  const triggerUpload = () => {
    // iter-184 P1 — surface a calm operational toast when the caller's
    // visibility doesn't include upload. The backend also enforces
    // (403 on POST /documents) — this is the UX-side guard.
    if (access && access.loading === false && access.can_upload_documents === false) {
      toast.message("Read-only access", {
        description: "Your access level on this deal doesn't include uploads.",
      });
      return;
    }
    fileInputRef.current?.click();
  };

  /* Tab content */
  const renderTab = () => {
    if (tab === "flow") {
      return (
        <FlowStream
          deal={displayDeal}
          contacts={contacts}
          currentUserId={user?.id || deal.agentId}
          onUpload={() => setTab("documents")}
          onChaseRequest={setChaseItem}
        />
      );
    }
    if (tab === "history") {
      return (
        <div className="space-y-4">
          <OperationalVitals dealId={deal.id} />
          <RoomHistory dealId={deal.id} />
        </div>
      );
    }
    if (tab === "details") {
      return (
        <div className="rounded-2xl p-6" style={cardStyle} data-testid="dealroom-tab-details-content">
          <h3 className="text-[14px] font-bold" style={{ color: LIGHT.textPrimary }}>Deal details</h3>
          <p className="text-[11.5px] mt-1" style={{ color: LIGHT.textMuted }}>Read-only metadata for this transaction.</p>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 mt-4">
            <DetailRow label="Property" value={property?.address || "—"} />
            <DetailRow label="Postcode" value={property?.postcode || "—"} />
            <DetailRow label="Tenure" value={property?.tenure || "—"} />
            <DetailRow label="Stage" value={deal.stage || "—"} />
            <DetailRow label="Status" value={deal.status || "Active"} />
            <DetailRow label="Chain Health" value={`${deal.chainHealth ?? "—"}`} />
          </dl>
        </div>
      );
    }
    if (tab === "milestones") {
      return (
        <ProgressionTimeline
          deal={deal}
          documents={documents}
          docRequests={docRequests}
          intel={intel}
          momentum={momentum}
        />
      );
    }
    if (tab === "golden_path") {
      return <GoldenPath onBack={() => nav("/deal-rooms")} />;
    }
    if (tab === "participants") {
      return (
        <ParticipantActivityPanel
          participants={participants}
          activity={displayDeal.activity || []}
          awaiting={[]}
          onInvite={() => setParticipantsOpen(true)}
          onChase={(p) => {
            setChaseItem({
              id: `participant-${p.user_id || p.id || p.email}`,
              title: `Chase ${p.full_name || p.email}`,
              owner: p.full_name || p.email,
              source: "participants_tab",
            });
          }}
          onMessage={(p) => {
            const name = (p.full_name || p.email || "").split(" ")[0];
            presetComposer("update", name ? `${name}, ` : "");
            setTab("flow");
            setTimeout(() => {
              document.querySelector('[data-testid="dealroom-composer-input"]')?.focus();
            }, 80);
          }}
        />
      );
    }
    if (tab === "documents") {
      return (
        <div className="space-y-5" data-testid="dealroom-tab-documents-content">
          {/* iter-184 P1 — visibility-aware upload surface. */}
          {deal ? (
            <DocumentHub
              deal={{ ...deal, documents: documents || [] }}
              access={access}
            />
          ) : null}
          <DocumentRequestsPanel
            requests={docRequests}
            documents={documents}
            onCreate={handleCreateRequest}
            onApprove={handleApproveRequest}
            onReject={handleRejectRequest}
            onFulfill={handleFulfillRequest}
            onCancel={handleCancelRequest}
            onDelete={handleDeleteRequest}
            onUpload={triggerUpload}
            onDownloadDoc={handleDocumentDownload}
            onDeleteDoc={handleDocumentDelete}
            canUpload={access?.can_upload_documents !== false}
          />
          {/* iter-184 P1 — Internal notes panel. Gated server-side;
              shows calm restricted-state placeholder for external. */}
          {deal ? <InternalNotesPanel dealId={deal.id} access={access} /> : null}
        </div>
      );
    }
    if (tab === "tasks") {
      return (
        <div className="rounded-2xl p-6" style={cardStyle} data-testid="dealroom-tab-tasks-content">
          <h3 className="text-[14px] font-bold" style={{ color: LIGHT.textPrimary }}>Tasks</h3>
          <p className="text-[11.5px] mt-1" style={{ color: LIGHT.textMuted }}>
            {counts.tasks} open · {tasks.length - counts.tasks} completed
          </p>
          {tasks.length === 0 ? (
            <p className="text-[12px] text-center py-8" style={{ color: LIGHT.textSubtle }}>No tasks yet.</p>
          ) : (
            <ul className="mt-4 space-y-2">
              {tasks.map((t) => (
                <li key={t.id} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: "#F8FAFC", border: `1px solid ${LIGHT.border}` }}>
                  <span className="w-4 h-4 rounded border" style={{ background: t.status === "done" ? "#10B981" : "#FFFFFF", borderColor: t.status === "done" ? "#10B981" : LIGHT.borderStrong }} />
                  <p className={`text-[12.5px] flex-1 truncate ${t.status === "done" ? "line-through" : ""}`} style={{ color: t.status === "done" ? LIGHT.textSubtle : LIGHT.textPrimary }}>{t.title}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }
    if (tab === "notes") {
      return (
        <div className="rounded-2xl p-6" style={cardStyle} data-testid="dealroom-tab-notes-content">
          <h3 className="text-[14px] font-bold" style={{ color: LIGHT.textPrimary }}>Notes</h3>
          <p className="text-[12px] mt-2 whitespace-pre-wrap leading-relaxed" style={{ color: deal.notes ? LIGHT.textPrimary : LIGHT.textSubtle }}>
            {deal.notes || "No internal notes yet."}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen flex" style={{ background: LIGHT.bg, color: LIGHT.textPrimary }} data-testid="dealroom-page">
      <Sidebar />
      <div className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out flex-1 flex flex-col min-w-0 relative">
        {/* ─── Ambient operational atmosphere (light, restrained) ───
            Three softly-layered radial blooms create depth without
            altering the approved layout. Inspired by the cinematic
            login-canvas reference — quiet, premium, embedded. */}
        <span
          aria-hidden="true"
          className="absolute top-0 right-0 w-[520px] h-[380px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(124,58,237,0.09), transparent 70%)" }}
        />
        <span
          aria-hidden="true"
          className="absolute top-[18%] -left-24 w-[420px] h-[420px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(56,189,248,0.05), transparent 72%)" }}
        />
        <span
          aria-hidden="true"
          className="absolute top-0 left-1/3 w-[520px] h-[260px] rounded-full blur-3xl pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(167,139,250,0.04), transparent 75%)" }}
        />
        {/* Faint top edge sheen — mirrors the login canvas crescent */}
        <span
          aria-hidden="true"
          className="absolute top-0 left-0 right-0 h-px pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(167,139,250,0.18) 50%, transparent 100%)",
          }}
        />

        <MobileStickyHeader deal={deal} property={property} onBack={() => nav("/deals")} />

        <div className="hidden md:block">
          <PropertyHeader
            deal={deal}
            property={property}
            participants={participants}
            healthScore={deal.chainHealth ?? 90}
            momentum={intel ? { token: intel.momentum, label: intel.momentum_label } : null}
            onBack={() => nav("/deals")}
            onInvite={() => setParticipantsOpen(true)}
            onShare={() => {
              navigator.clipboard?.writeText(window.location.href).catch(() => null);
              toast.success("Deal Room link copied");
            }}
            onUpdate={() => {
              setTab("flow");
              setTimeout(() => {
                document.querySelector('[data-testid="dealroom-composer-input"]')?.focus();
              }, 80);
            }}
          />
        </div>
        <StageProgression deal={deal} />

        {/* iter-181 · Phase 2 — Since your last visit + Current blockers
         *  Sit directly below the StageProgression (hero / transaction
         *  timeline) and above the operational workspace grid. Calm,
         *  compact, premium SaaS — never noisy notifications. */}
        <div className="px-5 sm:px-7 lg:px-9 mt-3 space-y-3" data-testid="dealroom-phase2-strip">
          <SinceLastVisitStrip
            deal={displayDeal}
            documents={documents}
            intel={intel}
            lastVisitIso={intel?.last_visit_at || intel?.since_last_visit?.at}
            onJumpToTimeline={() => setTab("history")}
          />
          <CompactBlockerRail
            deal={displayDeal}
            documents={documents}
            docRequests={docRequests}
            intel={intel}
            onChase={(blocker) => {
              setChaseItem({
                id: `blocker-${blocker.id}`,
                title: `Chase ${blocker.owner}`,
                owner: blocker.owner,
                source: "blocker_rail",
                context: blocker.title,
              });
            }}
            onViewAll={() => setTab("tasks")}
          />
        </div>

        <div className="px-5 sm:px-7 lg:px-9 pt-2 pb-32 lg:pb-24 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6 lg:gap-8 relative">
          {/* Center pane */}
          <section className="min-w-0">
            {/* Mobile: sticky tab strip so users can switch tabs at any
                scroll depth. Desktop keeps its inline placement. */}
            <div
              className="lg:static lg:bg-transparent sticky top-12 z-20 -mx-5 sm:-mx-7 lg:mx-0 px-5 sm:px-7 lg:px-0"
              style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(12px)" }}
              data-testid="dealroom-tabs-sticky-wrap"
            >
              <ContentTabs active={tab} onChange={setTab} counts={counts} />
            </div>
            <div className="px-5 sm:px-7 lg:px-9 -ml-5 sm:-ml-7 lg:-ml-9 -mr-5 sm:-mr-7 lg:-mr-9 pt-5">
              <div className="px-5 sm:px-7 lg:px-9">
                {/* Operational status sentence — derived from data already
                    present on the deal. Renders only when there's enough
                    signal to compose a meaningful line. */}
                {tab === "flow" && (
                  <DealRoomStatusLine deal={displayDeal} docRequests={docRequests} />
                )}
                {/* Mission Control · Operational Summary Line (Phase 2)
                    Deterministic one-sentence operational state, calm
                    typography, conditional accent dot. */}
                {tab === "flow" && (
                  <OperationalSummaryLine dealId={deal?.id} />
                )}
                {/* "Since Your Last Visit" — operational continuity for
                    returning participants. Renders only when there are
                    real new updates; otherwise it's invisible. */}
                <DealRoomReturnRibbon
                  dealId={deal.id}
                  onJumpToHistory={() => setTab("history")}
                />
                {/* Mission Control · Recommended Next Actions (Phase 1)
                    The operational brain of the Deal Room — calm
                    one-click action cards with momentum + risk +
                    confidence + recovery value framing. */}
                {tab === "flow" && (
                  <RecommendedNextActions
                    dealId={deal?.id}
                    onActionSuccess={() => reloadDeal?.()}
                  />
                )}
                {/* Awaiting (operational, primary) — only on Flow tab */}
                {tab === "flow" && (
                  <div className="mb-5">
                    <AwaitingCard
                      deal={deal}
                      tasks={tasks}
                      documents={documents}
                      recentlyChasedIds={recentlyChasedIds}
                      onAction={() => setTab("documents")}
                      onMarkResolved={handleMarkAwaitingResolved}
                      onChase={handleAwaitingChase}
                      onEscalate={handleAwaitingEscalate}
                      onAddUpdate={handleAwaitingAddUpdate}
                      onRequestDoc={handleAwaitingRequestDoc}
                      onReassign={handleAwaitingReassign}
                    />
                  </div>
                )}
                {renderTab()}
              </div>
            </div>
            {tab === "flow" && (
              <div
                className="lg:static lg:bg-transparent fixed lg:relative bottom-0 left-0 right-0 z-30 lg:z-auto"
                style={{
                  background: "rgba(255,255,255,0.96)",
                  backdropFilter: "blur(14px)",
                  borderTop: `1px solid ${LIGHT.border}`,
                  paddingBottom: "env(safe-area-inset-bottom, 0)",
                }}
                data-testid="dealroom-composer-sticky-wrap"
              >
                <div className="px-5 sm:px-7 lg:px-9">
                  <TypingIndicator typing={intel?.typing || []} />
                </div>
                {/* iter-184 P1 — calm read-only state for participants
                    whose visibility excludes commenting. The composer
                    is replaced with a single subtle line so observers
                    still feel included, not rejected. */}
                {access && access.loading === false && access.can_comment === false ? (
                  <div
                    className="px-5 sm:px-7 lg:px-9 py-5 text-[12.5px] text-slate-500"
                    data-testid="dealroom-composer-readonly"
                  >
                    You have read-only access on this deal — comments and operational actions are paused for your view.
                  </div>
                ) : (
                  <FlowComposer
                    key={presetTick}
                    onSend={handleSend}
                    presetKind={presetKind}
                    presetText={presetText}
                    onTyping={(active) => {
                      if (!deal?.id) return;
                      api.post(`/api/deals/${deal.id}/typing`, { is_typing: active }).catch(() => null);
                    }}
                  />
                )}
              </div>
            )}
          </section>

          {/* Right intelligence panel */}
          <aside
            className="hidden xl:flex flex-col gap-4 sticky top-4 self-start pt-6 overflow-y-auto cf-scroll pr-1"
            style={{ maxHeight: "calc(100vh - 32px)" }}
            data-testid="dealroom-intel-panel"
          >
            {/* Operational Intelligence Rail — live signals for this deal */}
            <DealIntelligenceRail dealId={deal?.id} />
            <SellerConfidencePanel dealId={deal?.id} />
            <SolicitorPortalInvitesPanel dealId={deal?.id} />
            <DealCoordinationPanel dealId={deal?.id} />
            <DealPlaybooksPanel dealId={deal?.id} />
            <DealRecoveryPlanPanel dealId={deal?.id} />
            <DealForecastTrajectoryPanel dealId={deal?.id} />
            <DealMemoryPanel dealId={deal?.id} />
            <DealScenarioPanel dealId={deal?.id} />
            <DealRhythmPanel dealId={deal?.id} />
            <DealDecisionPanel dealId={deal?.id} />
            <SinceLastVisit data={intel?.since_last_visit} onMarkSeen={handleMarkSeen} />
            <MomentumEngine momentum={momentum} loading={!momentum} onAction={handleMomentumAction} />
            <FlowIntelligencePanel intel={intel} loading={!intel} deal={deal} />
            <ExchangeReadinessCard deal={deal} documents={documents} />
            <DealHealthSummary deal={deal} lastActivityTs={lastActivityTs} />
            <NextKeyDateCard deal={deal} />
            <ParticipantsLiveCard
              participants={participants}
              onInvite={() => setParticipantsOpen(true)}
              dealId={deal?.id}
            />
            <DocumentRequestsCard
              requests={docRequests}
              documents={documents}
              onCreate={handleCreateRequest}
              onApprove={handleApproveRequest}
              onReject={handleRejectRequest}
              onFulfill={handleFulfillRequest}
              onCancel={handleCancelRequest}
              onDelete={handleDeleteRequest}
            />
            <QuickActionsCard onAction={handleQuickAction} />
          </aside>
        </div>

        <BottomKPIBar deal={deal} property={property} lastActivityTs={lastActivityTs} mobileOffset={62} />

        {/* Mobile FAB — quick-compose primary action */}
        <MobileFAB
          onTap={() => {
            setTab("flow");
            // Smooth-scroll to top so the composer is in thumb reach
            window.scrollTo({ top: 0, behavior: "smooth" });
            // Focus the composer textarea after scroll settles
            setTimeout(() => {
              document.querySelector('[data-testid="dealroom-composer-input"]')?.focus();
            }, 320);
          }}
        />

        <div className="h-[68px]" aria-hidden="true" />
        {/* Mobile spacers: BottomKPIBar (78px) + global MobileBottomNav (62px + safe-area) */}
        <div
          className="md:hidden"
          aria-hidden="true"
          style={{ height: "calc(78px + 62px + env(safe-area-inset-bottom, 0px))" }}
        />
      </div>

      {/* Hidden file input — driven by triggerUpload() */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.zip"
        onChange={handleFileUpload}
        data-testid="dealroom-file-input"
      />

      {/* Participants invite modal */}
      {participantsOpen && (
        <ParticipantsModal
          open={participantsOpen}
          onClose={() => { setParticipantsOpen(false); reloadDeal(); }}
          deal={deal}
          property={property}
        />
      )}

      {/* Chase confirmation drawer */}
      <ChaseConfirmDrawer
        open={!!chaseItem}
        dealId={deal?.id}
        item={chaseItem}
        onClose={() => setChaseItem(null)}
        onSent={handleChaseSent}
      />
    </div>
  );
}

const DetailRow = ({ label, value }) => (
  <div>
    <dt className="text-[10px] font-bold uppercase tracking-[0.16em]" style={{ color: LIGHT.textSubtle }}>{label}</dt>
    <dd className="text-[12.5px] mt-1" style={{ color: LIGHT.textPrimary }}>{value}</dd>
  </div>
);
